import assertEventType from 'common/bento/lib/assertEventType';
import { MachineServices } from 'common/bento/types/MachineConfig';
import { apolloClient } from 'common/graphql/clients';
import getCurrentLegalBeneficiary from 'features/Onboarding/lib/getCurrentLegalBeneficiary';

import { UpdatePersonalInfoBusinessRoleDocument } from '../graphql/mutations/updatePersonalInfoBusinessRole.gql';
import { UpdatePersonalInfoNameDocument } from '../graphql/mutations/updatePersonalInfoName.gql';
import { UpdatePersonalInfoUserProfileDocument } from '../graphql/mutations/updatePersonalInfoUserProfile.gql';
import { getHasAlreadyFilledProfile } from '../libs';
import { PersonalInfoErrorCode } from './errors';
import { Model } from './model';

export enum Service {
  SaveBusinessRole = 'saveBusinessRole',
  SaveDirectorSelection = 'saveDirectorSelection',
  SendName = 'sendName',
}

const services: MachineServices<Model, Service> = {
  saveBusinessRole: async ({ getModuleData }, event) => {
    assertEventType(event, 'SUBMIT_BUSINESS_ROLE');

    const { company, companyUser } = getModuleData().viewer;
    if (!company) {
      throw new Error(`No company in saveBusinessRole`);
    }
    if (!companyUser) {
      throw new Error(`No companyUser in saveBusinessRole`);
    }

    const legalBeneficiary = getCurrentLegalBeneficiary(
      company.legalBeneficiaries,
    );

    if (!legalBeneficiary) {
      throw new Error(`No legalBeneficiary in saveBusinessRole`);
    }

    const { isDirector, isMajorShareholder } = event.data;

    return apolloClient.mutate({
      mutation: UpdatePersonalInfoBusinessRoleDocument,
      variables: {
        input: {
          companyProfileId: company.companyProfileId,
          id: companyUser.companyUserId,
          isDirector,
          isMajorShareholder,
        },
      },
    });
  },

  saveDirectorSelection: async ({ getModuleData }, event) => {
    const { profile } = getModuleData().viewer;
    assertEventType(event, 'SUBMIT_DIRECTOR_SELECTION');

    /**
     * If the user identified as a publicly listed director,
     * and has not already filled his own info (= in another account),
     * save these infos in his profile.
     */
    if (getHasAlreadyFilledProfile(profile) === false) {
      await apolloClient.mutate({
        mutation: UpdatePersonalInfoUserProfileDocument,
        variables: {
          profile: {
            firstName: event.data.firstName,
            gender: event.data.gender ?? undefined,
            lastName: event.data.lastName,
          },
        },
      });

      if (!event.data.gender) {
        // We throw an error to avoid skipping the NAME state as we want
        // the user to confirm its gender on this screen.
        // The error is handled in the state machine.
        throw new Error(PersonalInfoErrorCode.DIRECTOR_MISSING_GENDER);
      }
    }
  },

  sendName: async (context, event) => {
    if (event.type !== 'SUBMIT_PERSONAL_NAME') {
      throw new Error(`sendName called on event ${event.type}`);
    }

    const { firstName, gender, lastName } = event.data;

    return apolloClient.mutate({
      mutation: UpdatePersonalInfoNameDocument,
      variables: {
        input: {
          firstName,
          gender,
          lastName,
        },
      },
    });
  },
};

export default services;
